import React from 'react';
import { POWERED_BY_SNOWFLAKE } from '../../../../content/enterpriseMessaging';
import SecurityScope from './SecurityScope';

const analyticsLayers = [
  {
    step: '1',
    title: 'React Analytics Application',
    description:
      'Split-screen product surface, copilot chat for on-demand answers with in-thread visualizations, plus an embedded BI report gallery and editable query-native charts.',
  },
  {
    step: '2',
    title: 'Snowflake Cortex Intelligence',
    description:
      'Cortex Intelligence agents map intent to certified HR metrics with read-only governed SQL, validated before execution, not open-ended warehouse exploration.',
  },
  {
    step: '3',
    title: 'Embedded BI & Dynamic Visualizations',
    description:
      'Pre-approved executive bookmarks activated per question with dynamic filters; optional on-demand charts beneath the embed when query results support them.',
  },
];

const techStack = [
  'Snowflake Cortex',
  'Power BI Embedded',
  'Workday REST API',
  'SuccessFactors OData',
  'Secure Views',
  'REPORTING / REPORTING_SECURE',
  'Microsoft Fabric',
  'AWS · Azure',
];

const cloudPlatforms = [
  { short: 'AWS', description: 'Cloud storage, ingestion, and orchestration for HR data pipelines.' },
  { short: 'Azure', description: 'Identity, networking, and hybrid services connecting HRIS to Snowflake.' },
  { short: 'Fabric', description: 'Unified analytics layer for hybrid reporting and Copilot experiences.' },
  { short: 'Data Factory', description: 'Pipeline orchestration for HR source ingestion and warehouse loading.' },
];

export default function TechnicalFoundation() {
  return (
    <>
      <section id="architecture" className="py-16 md:py-20">
        <div className="container-x">
          <div className="max-w-3xl mb-10">
            <p className="eyebrow">Architecture</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-ink-900">
              Snowflake-native layers from chat to certified reporting views
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-ink-600">
              Semantic HR connects the React analytics application, Cortex Intelligence agents, and embedded
              Power BI on the same governed semantic models in Snowflake.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 lg:mb-16">
            {analyticsLayers.map((layer) => (
              <div key={layer.title} className="relative rounded-xl border border-ink-200 bg-white p-6 pt-8">
                <span className="absolute -top-3 left-5 flex h-6 w-6 items-center justify-center rounded-full bg-brand-700 text-xs font-semibold text-white">
                  {layer.step}
                </span>
                <p className="text-base font-semibold text-ink-900">{layer.title}</p>
                <p className="mt-2 text-sm text-ink-600 leading-relaxed">{layer.description}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            <div>
              <p className="eyebrow">Technology stack</p>
              <h3 className="mt-3 text-2xl font-semibold tracking-tight text-ink-900">
                Enterprise-grade foundation
              </h3>
              <p className="mt-4 text-base text-ink-600 leading-relaxed">
                Snowflake at the center, AWS and Microsoft Azure for cloud infrastructure, and Fabric
                for integration, designed to scale with your organization and connect to existing HRIS environments.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {techStack.map((tech) => (
                  <span key={tech} className="badge-neutral text-sm">{tech}</span>
                ))}
              </div>
              <div className="mt-8 flex min-w-0 max-w-full items-center gap-4 rounded-xl border border-ink-200 bg-white p-4">
                <img
                  src={POWERED_BY_SNOWFLAKE.src}
                  alt={POWERED_BY_SNOWFLAKE.alt}
                  className="h-8 w-auto max-w-full flex-shrink-0 object-contain"
                />
                <p className="min-w-0 flex-1 break-words text-sm text-ink-600">
                  Cortex AI, Intelligence, secure views, and warehouse architecture on Snowflake.
                </p>
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold text-ink-700 mb-4">Cloud &amp; orchestration</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {cloudPlatforms.map((platform) => (
                  <div key={platform.short} className="rounded-lg border border-ink-100 bg-surface-muted px-4 py-3">
                    <p className="text-xs font-semibold text-ink-900">{platform.short}</p>
                    <p className="mt-1 text-xs text-ink-500 leading-relaxed">{platform.description}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {['Power BI', 'Workday', 'SAP SuccessFactors'].map((badge) => (
                  <span key={badge} className="badge-neutral">{badge}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      <SecurityScope />
    </>
  );
}
